/**
 * pdf.js helpers for the Digital Twin floor-plan surface (Sprint 3 D3).
 *
 * Renders page 1 of a PDF floor plan to a canvas so the normalized polygon
 * overlay can sit on top — same code path as an <img> floor plan, just a
 * different base layer. Worker is served from public/ (copied at build) so it
 * works offline / on ECS without a CDN.
 */
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/** Load page 1 of a PDF from a URL string or { data: ArrayBuffer }. */
async function loadPage1(src) {
    const task = pdfjsLib.getDocument(src);
    const pdf = await task.promise;
    return pdf.getPage(1);
}

/**
 * Render page 1 of `url` into `canvas`, scaled so the bitmap is ~targetWidth px
 * wide (for crispness). Returns the PDF's intrinsic { width, height } at scale 1.
 */
export async function renderPdfToCanvas(url, canvas, targetWidth) {
    const page = await loadPage1(url);
    const base = page.getViewport({ scale: 1 });
    const scale = targetWidth && base.width ? targetWidth / base.width : 1.5;
    const viewport = page.getViewport({ scale });
    const ctx = canvas.getContext('2d');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    return { width: Math.round(base.width), height: Math.round(base.height) };
}

/**
 * Intrinsic pixel dimensions of an uploaded floor-plan File, used to populate
 * property.floor_plan.width/height at upload time. Returns {} if undetectable.
 */
export async function getAssetDimensions(file) {
    if (file.type === 'application/pdf') {
        const data = await file.arrayBuffer();
        const page = await loadPage1({ data });
        const vp = page.getViewport({ scale: 1 });
        return { width: Math.round(vp.width), height: Math.round(vp.height) };
    }
    // image / svg
    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            const dims = (img.naturalWidth && img.naturalHeight)
                ? { width: img.naturalWidth, height: img.naturalHeight }
                : {};
            URL.revokeObjectURL(objectUrl);
            resolve(dims);
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve({}); };
        img.src = objectUrl;
    });
}
